import "./EventList.css";

import * as React from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { ErrorBoundary } from "../../../errors/ErrorBoundary";
import Modal, { ModalSize } from "../../../misc/components/modal";
import {
    CloseEventListDialogAction,
    JumpToTimePointAction,
    RemovePeriodEventAction,
    RemovePointEventAction,
    SetDuplicateVerifiedAction
} from "../actions";
import { AnalyseState } from "../interfaces/state";
import { PeaksPeriod, PeaksPoint } from "../interfacesPeaks/PeaksJS";
import { EventListTable } from "./EventListTable";
import { EventTotals } from "./EventTotals";
import { Query } from '../../../misc/components/queryTable/Query';
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../../misc/api/WebPortalTokenClient";
import { Form } from "informedv4";
import { QueryOrder } from "../../../misc/api/apiTypes";

export interface EventListProps {
    isVisible: boolean;
    pointEvents: PeaksPoint[];
    periodEvents: PeaksPeriod[];
    selectedSegment: number;
    dispatch?: Dispatch<AnalyseState>;
}

export const EventList = (props: EventListProps) => {
    const [periodTableQuery, setPeriodTableQuery] = React.useState<Query<any>>();
    const [pointTableQuery, setPointTableQuery] = React.useState<Query<any>>();
    const [pointSortOrder, setPointSortOrder] = React.useState<QueryOrder | null>(null);
    const [periodSortOrder, setPeriodSortOrder] = React.useState<QueryOrder | null>(null);

    const legacyClient = useWebPortalLegacyClient();

    if (props.isVisible) {

        return (
            <Form validateOn="submit">
            <Modal
                show={true}
                size={ModalSize.Large}
                header="Event List"
                closeModalHandler={() =>
                    props.dispatch(CloseEventListDialogAction.create())
                }>
                <div className="modal-body">
                    <div className="row">
                        <div className="col-md-12">
                            <EventTotals
                                    pointEvents={props.pointEvents}
                                    periodEvents={props.periodEvents}
                                    isDisputedView={false}

                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <h3 className="text-primary">
                                Individual Period Events
                            </h3>
                            <ErrorBoundary>
                                <EventListTable
                                    key="PeriodsEventsTable"
                                    startTimeColumnName="Waveform Start Time"
                                    timestampColumn="startTime"
                                    withEndTime={true}
                                    events={props.periodEvents}
                                    onJumpToEventClick={timepoint => {
                                        props.dispatch(
                                            JumpToTimePointAction.create(
                                                timepoint
                                            )
                                        );
                                        props.dispatch(
                                            CloseEventListDialogAction.create()
                                        );
                                    }}
                                    onDeleteEventClick={id =>
                                        props.dispatch(
                                            RemovePeriodEventAction.create(
                                                legacyClient,
                                                id
                                            )
                                        )
                                    }
                                    query={periodTableQuery}
                                    selectedSegment={props.selectedSegment}
                                    sortOrder={periodSortOrder}
                                    onSortChanged={setPeriodSortOrder}
                                />
                            </ErrorBoundary>
                        </div>
                        <div className="col-md-6">
                            <h3 className="text-primary">
                                Individual Point Events
                            </h3>
                                <ErrorBoundary>
                                    <EventListTable
                                        key="PointsEventsTable"
                                        startTimeColumnName="Waveform Time"
                                        timestampColumn="timestamp"
                                        withEndTime={false}
                                        events={props.pointEvents}
                                        renderRawTime
                                        onJumpToEventClick={timepoint => {
                                            props.dispatch(
                                                JumpToTimePointAction.create(
                                                    timepoint
                                                )
                                            );
                                            props.dispatch(
                                                CloseEventListDialogAction.create()
                                            );
                                        }}
                                        onDeleteEventClick={id =>
                                            props.dispatch(
                                                RemovePointEventAction.create(
                                                    legacyClient,
                                                    id
                                                )
                                            )
                                        }
                                        onConfirmDuplicateThresholdEventClick={async id => {
                                            props.dispatch(SetDuplicateVerifiedAction.create(id));
                                        }}
                                        query={pointTableQuery}
                                        selectedSegment={props.selectedSegment}
                                        sortOrder={pointSortOrder}
                                        onSortChanged={setPointSortOrder}
                                     />
                            </ErrorBoundary>
                        </div>
                    </div>
                </div>
                <div className="modal-footer">
                    <button
                        className="btn btn-primary"
                        onClick={() =>
                            props.dispatch(
                                CloseEventListDialogAction.create()
                            )
                        }>
                        Close
                    </button>
                </div>
                </Modal>
            </Form>
        );
    } else {
        return null;
    }
}

function mapStateToProps(state: AnalyseState) {

    let pointEvents = state.core.waveformPoints.map(pp => {
        return {
            timestamp: pp.timestamp,
            rawTimestamp: state.core.rawTimestampPoints ? state.core.rawTimestampPoints.get(pp.id) : 0,
            color: pp.color,
            editable: pp.editable,
            eventTypeId: pp.eventTypeId,
            eventTypeName: pp.eventTypeName,
            id: pp.id,
            isPoint: pp.isPoint,
            labelText: pp.labelText,
            layerPosition: pp.layerPosition,
            editMode: pp.editMode,
            withinDuplicateThreshold: pp.withinDuplicateThreshold,
            confirmedValidDuplicate: pp.confirmedValidDuplicate,
        } as PeaksPoint;
    });

    let periodEvents = state.core.waveformPeriods.map(pp => {
        return {
            color: pp.color,
            editable: pp.editable,
            eventTypeId: pp.eventTypeId,
            eventTypeName: pp.eventTypeName,
            id: pp.id,
            isPoint: pp.isPoint,
            labelText: pp.labelText,
            layerPosition: pp.layerPosition,
            editMode: pp.editMode,
            startTime: pp.startTime,
            endTime: pp.endTime,
            withinDuplicateThreshold: pp.withinDuplicateThreshold,
            confirmedValidDuplicate: pp.confirmedValidDuplicate,
        } as PeaksPeriod;
    })

    return {
        isVisible: state.core.isEventListOpen,
        pointEvents,
        periodEvents,
        selectedSegment: state.core.selectedSegment
    };
}

export default connect<EventListProps, any>(mapStateToProps)(EventList);
